/**
 * PATH: packages/email-hub/src/feature/lib/imageClipboardPaste.ts
 * FILE_ID: EMAIL-HUB-LIB-CLIPBOARD-PASTE-001
 * PURPOSE: Pure I→O helpers that rewrite PASTED `text/html` so its images
 *          both display in the editor and travel on send, and so an
 *          Office/Outlook table-layout signature keeps its look.
 * WHY:     Split out of imageClipboard.ts (which exceeded the 500-line
 *          working limit). This is the "paste a signature/snippet" half:
 *          <img> src classification + rewrite, CSS <style> flattening,
 *          and the missing-image affordance. The clipboard/blob/embed
 *          primitives stay in imageClipboard.ts and are imported here.
 * FLOW:
 *   pasted html ─▶ DOMParser ─▶ flatten <style> ─▶ rewrite <img> ─▶ html
 * DEPENDENCIES:
 *   - ./imageClipboard: fetchImageBlob, compressImageToDataUrl, blobToFile
 * RELATED:
 *   - components/RichTextEditor.tsx: handlePaste consumes these.
 */
import {
  fetchImageBlob,
  compressImageToDataUrl,
  blobToFile,
} from './imageClipboard'

/** True when pasted HTML carries at least one ``<img>``. Lets the editor
 *  decide to INTERCEPT + inline-fix the paste instead of falling through
 *  to ProseMirror's default — which renders a ``cid:``/expired ``src`` as a
 *  broken-image icon and never inlines it on send. I→O: html → boolean. */
export function htmlHasImage(html: string | null | undefined): boolean {
  return /<img\b[^>]*>/i.test(html || '')
}

/** Classify an `<img>` `src` for paste handling:
 *   - ``data``   : inline bytes (the send path turns these into a cid:
 *                  attachment) — keep verbatim.
 *   - ``remote`` : absolute http(s) URL — loads in the editor + most
 *                  clients; we try to inline it so it also travels.
 *   - ``dead``   : cid:/blob:/file:/relative/empty — can NEVER resolve
 *                  once pasted into our editor, so it must be rescued
 *                  from a clipboard blob or dropped (no broken icon).
 *  I→O: src → 'data' | 'remote' | 'dead'. */
export function classifyImgSrc(src: string | null | undefined): 'data' | 'remote' | 'dead' {
  const s = (src || '').trim()
  if (/^data:image\//i.test(s)) return 'data'
  if (/^https?:\/\//i.test(s)) return 'remote'
  return 'dead'
}

/** CSS properties we will lift out of a pasted ``<style>`` block onto the
 *  matching element's inline ``style`` attribute. Deliberately an
 *  allowlist of LAYOUT + TEXT presentation only: Office/Outlook
 *  signatures lean on class-based table styles that are dropped the moment
 *  we keep just ``body.innerHTML``. Anything not here (position, float,
 *  z-index, transforms, …) is ignored so we never import surprising or
 *  unsafe declarations. I→O contributor: prop name → kept?. */
export const ALLOWED_INLINE_STYLE_PROPS: ReadonlySet<string> = new Set([
  'color', 'background-color',
  'font-family', 'font-size', 'font-weight', 'font-style', 'line-height',
  'text-align', 'text-decoration', 'text-transform', 'letter-spacing',
  'vertical-align', 'white-space',
  'width', 'height', 'min-width', 'max-width',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
  'border-color', 'border-width', 'border-style', 'border-radius',
  'border-collapse', 'border-spacing',
])

/** Reject a declaration value that could smuggle a remote/script payload
 *  through the inliner (``url(...)`` trackers, legacy IE ``expression()``,
 *  ``javascript:``). Pure: value → safe?. */
function isSafeStyleValue(value: string): boolean {
  return !/url\s*\(|expression\s*\(|javascript:/i.test(value)
}

/** Parse a ``prop: value; …`` declaration string into a map, dropping
 *  ``!important`` and unsafe values. Pure: cssText → {prop: value}. */
export function parseStyleDeclarations(cssText: string): Record<string, string> {
  const out: Record<string, string> = {}
  for (const chunk of (cssText || '').split(';')) {
    const idx = chunk.indexOf(':')
    if (idx < 0) continue
    const prop = chunk.slice(0, idx).trim().toLowerCase()
    const value = chunk.slice(idx + 1).replace(/!important/gi, '').trim()
    if (!prop || !value) continue
    if (!isSafeStyleValue(value)) continue
    out[prop] = value
  }
  return out
}

/** Merge allowlisted declarations onto an element's inline style WITHOUT
 *  overriding anything already set inline (inline is the more-specific,
 *  author-intended value). Mutates ``el``. */
function mergeAllowedInlineStyle(
  el: Element, decls: Record<string, string>,
): void {
  const existing = parseStyleDeclarations(el.getAttribute('style') || '')
  let changed = false
  for (const [prop, value] of Object.entries(decls)) {
    if (!ALLOWED_INLINE_STYLE_PROPS.has(prop)) continue
    if (prop in existing) continue
    existing[prop] = value
    changed = true
  }
  if (!changed) return
  const merged = Object.entries(existing)
    .map(([p, v]) => `${p}:${v}`)
    .join(';')
  el.setAttribute('style', merged)
}

/** Selectors safe to resolve with ``querySelectorAll`` and to attribute
 *  to a single element shape: a lone tag / .class / #id, optionally
 *  combined (``td.foo``). Descendant/sibling/pseudo/attribute selectors
 *  are skipped — too cascade-dependent to flatten faithfully. */
function isFlattenableSelector(sel: string): boolean {
  const s = sel.trim()
  if (!s) return false
  if (/[\s>+~:[\]*]/.test(s)) return false
  return /^[a-z]+$|^[.#][\w-]+$|^[a-z]+[.#][\w-]+$/i.test(s)
}

/** Lift class/tag/id ``<style>`` rules in a parsed document onto matching
 *  elements' inline styles, then strip the now-redundant ``<style>``
 *  blocks. This is what keeps a pasted Office/Outlook table-layout
 *  signature looking right after we serialise only ``body.innerHTML``
 *  (head styles would otherwise be discarded). Mutates ``doc``. */
export function inlineDocumentStyles(doc: Document): void {
  const styleEls = Array.from(doc.querySelectorAll('style'))
  if (styleEls.length === 0) return
  const cssText = styleEls.map((s) => s.textContent || '').join('\n')
    .replace(/\/\*[\s\S]*?\*\//g, '') // strip comments
  // Flat ``selector { decls }`` blocks only (skip @-rules / nesting).
  const blockRe = /([^{}]+)\{([^{}]*)\}/g
  let m: RegExpExecArray | null
  while ((m = blockRe.exec(cssText)) !== null) {
    const selectorGroup = m[1].trim()
    if (selectorGroup.startsWith('@')) continue
    const decls = parseStyleDeclarations(m[2])
    if (Object.keys(decls).length === 0) continue
    for (const sel of selectorGroup.split(',')) {
      if (!isFlattenableSelector(sel)) continue
      let matches: Element[]
      try {
        matches = Array.from(doc.querySelectorAll(sel.trim()))
      } catch {
        continue
      }
      for (const el of matches) mergeAllowedInlineStyle(el, decls)
    }
  }
  for (const s of styleEls) s.remove()
}

/** Build the small inline note shown in place of an image we could NOT
 *  paste (a ``cid:`` logo with no clipboard blob to rescue it from).
 *  Better than a silent drop: the operator sees the image was meant to be
 *  there and can re-insert it with the image button. Returns a detached
 *  ``<span>`` in ``doc``. */
export function buildDeadImagePlaceholder(doc: Document, alt: string): HTMLElement {
  const span = doc.createElement('span')
  span.setAttribute('data-qte-missing-image', '1')
  span.setAttribute(
    'style',
    'display:inline-block;padding:1px 6px;border:1px dashed #94a3b8;'
    + 'border-radius:4px;color:#64748b;font-size:12px;',
  )
  const label = (alt || '').trim()
  span.textContent = label
    ? `🖼 ${label} — image couldn’t be pasted, use the image button to insert it`
    : '🖼 Image couldn’t be pasted — use the image button to insert it'
  return span
}

export interface InlinePastedImagesOptions {
  /** Data URLs for image bytes that ALSO rode the clipboard (e.g. a
   *  signature copied from a mail client that hands over BOTH the markup
   *  and the image blob). Consumed in document order to rescue ``dead``
   *  (cid:/blob:/relative) srcs the markup still references. */
  rescueDataUrls?: string[]
  /** Fetch remote bytes (injected in tests). Default: ``fetchImageBlob``. */
  fetchBlob?: (src: string) => Promise<Blob | null>
  /** Second-chance remote fetch, e.g. through the same-origin backend
   *  image proxy (callers inject ``fetchImageViaProxy``; default none —
   *  this module stays transport-free). Runs only when ``fetchBlob``
   *  came back empty — the common corporate-signature case: a logo on
   *  a host with no CORS headers (or an auth-gated Outlook URL) that a
   *  direct browser fetch can never read
   *  (BUG-2026-07-06-SIGNATURE-REMOTE-LOGO-NOT-INLINED). */
  fetchBlobViaProxy?: (src: string) => Promise<Blob | null>
  /** Blob → capped data: URL (injected in tests). Default: downscale +
   *  encode via ``compressImageToDataUrl`` so the inlined body stays small. */
  toDataUrl?: (blob: Blob) => Promise<string>
  /** Per-remote-image budget (ms); on timeout the original URL is kept so
   *  the image still loads remotely. Default 8000. */
  timeoutMs?: number
  /** When a ``dead`` img can't be rescued, replace it with a visible
   *  "couldn't paste this image" note instead of removing it silently.
   *  Default ``true``. Set false for the old silent-drop behaviour. */
  surfaceDeadImages?: boolean
}

/** Rewrite every `<img>` `src` in pasted HTML so the image both DISPLAYS
 *  in the editor and TRAVELS on send. This is what makes "paste my
 *  signature" actually work: a signature copied from a mail client comes
 *  as `text/html` whose `<img>` points at a `cid:` (or an expired signed
 *  URL) the browser can't load — default paste shows a broken icon and,
 *  on send, ships nothing.
 *
 *  Per `<img>` (concurrently, but rescue is consumed in document order):
 *   - ``data``   → kept verbatim.
 *   - ``remote`` → fetched → capped data: URL (so it inlines on send);
 *                  on fetch failure/timeout the original URL is kept.
 *   - ``dead``   → replaced by the next ``rescueDataUrls`` entry if one
 *                  is available, else a visible affordance (or removed
 *                  when ``surfaceDeadImages`` is false).
 *
 *  Also flattens Office/Outlook class-based ``<style>`` rules onto inline
 *  styles so a pasted table-layout signature keeps its look.
 *
 *  Pure-ish: all IO is injected/overridable; returns the rewritten HTML
 *  fragment. SSR/no-DOMParser → returns the input unchanged.
 *  I→O: (html, opts) → rewritten html. */
export async function inlinePastedHtmlImages(
  html: string,
  opts: InlinePastedImagesOptions = {},
): Promise<string> {
  if (typeof DOMParser === 'undefined') return html
  const {
    rescueDataUrls = [],
    fetchBlob = fetchImageBlob,
    fetchBlobViaProxy,
    toDataUrl = (blob: Blob) => compressImageToDataUrl(blobToFile(blob)),
    timeoutMs = 8000,
    surfaceDeadImages = true,
  } = opts
  const rescue = [...rescueDataUrls]
  const doc = new DOMParser().parseFromString(html, 'text/html')
  // Flatten Office/Outlook class-based <style> rules onto inline styles
  // BEFORE serialising body.innerHTML, so a pasted table-layout signature
  // keeps its look (head styles would otherwise be silently discarded).
  inlineDocumentStyles(doc)
  const imgs = Array.from(doc.querySelectorAll('img'))
  await Promise.all(
    imgs.map(async (img) => {
      const kind = classifyImgSrc(img.getAttribute('src'))
      if (kind === 'data') return
      if (kind === 'dead') {
        const next = rescue.shift()
        if (next) { img.setAttribute('src', next); return }
        // No bytes to rescue it with: surface a visible affordance instead
        // of a silent drop, so the operator knows an image is missing.
        if (surfaceDeadImages) {
          img.replaceWith(buildDeadImagePlaceholder(doc, img.getAttribute('alt') || ''))
        } else {
          img.remove()
        }
        return
      }
      const src = img.getAttribute('src') as string
      // Direct fetch first (cheap, works for CORS-friendly hosts), then
      // the backend image proxy — the reader's remote-recovery strategy
      // applied at PASTE time, so a corporate signature logo inlines.
      let blob = await withImageTimeout(fetchBlob(src), timeoutMs, null)
      if (!blob && fetchBlobViaProxy) {
        blob = await withImageTimeout(fetchBlobViaProxy(src), timeoutMs, null)
      }
      if (!blob) return
      const dataUrl = await withImageTimeout(toDataUrl(blob), timeoutMs, '')
      if (dataUrl) img.setAttribute('src', dataUrl)
    }),
  )
  return doc.body.innerHTML
}

/** Resolve ``p``, but never wait longer than ``ms`` — on timeout (or a
 *  rejection) resolve ``fallback`` instead. Keeps a slow/blocked remote
 *  image from hanging the whole paste. */
function withImageTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    let settled = false
    const timer = setTimeout(() => {
      if (!settled) { settled = true; resolve(fallback) }
    }, ms)
    p.then(
      (v) => { if (!settled) { settled = true; clearTimeout(timer); resolve(v) } },
      () => { if (!settled) { settled = true; clearTimeout(timer); resolve(fallback) } },
    )
  })
}
